import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { getIcon } from "../lib/icons";
import api from "../lib/api";
import { useLang } from "../lib/i18n";

export default function SearchModal({ open, onOpenChange }) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      api
        .get("/products/search", { params: { q: q.trim() } })
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col p-0"
        style={{ border: "2px solid #0F172A", borderRadius: 20 }}
        data-testid="search-modal"
      >
        <VisuallyHidden>
          <DialogTitle>{t("search.title")}</DialogTitle>
          <DialogDescription>{t("search.desc")}</DialogDescription>
        </VisuallyHidden>
        <div className="p-4 border-b-2 border-slate-900 flex items-center gap-2 shrink-0">
          <Search size={18} className="text-slate-500 shrink-0" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 outline-none text-base bg-transparent"
            data-testid="search-input"
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="search-clear-btn" aria-label="Clear">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-auto p-2" data-testid="search-results">
          {loading && <div className="text-center text-sm text-slate-500 py-8">{t("search.searching")}</div>}
          {!loading && q.trim() && results.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8" data-testid="search-empty">
              {t("search.no_results")}
            </div>
          )}
          {!loading &&
            results.map((p) => {
              const Icon = getIcon(p.icon);
              const imgSrc = p.image_url ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : null;
              return (
                <Link
                  key={p.id}
                  to={`/products/${p.slug}`}
                  onClick={() => onOpenChange(false)}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-yellow-50 transition-colors"
                  data-testid={`search-result-${p.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
                    style={{ background: p.color, border: "2px solid #0F172A" }}
                  >
                    {imgSrc ? (
                      <img src={imgSrc} alt={p.name} className="w-full h-full object-contain" />
                    ) : (
                      <Icon size={22} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">
                      {p.category} • {p.price.toFixed(0)} EGP
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
